import { writable, type Writable, type Readable } from "svelte/store"

export enum ReadyTrackerStatus {
    NONE,
    SOME,
    ALL,
}

/**
 * If we need to keep track of whether all, some, or none of some
 * highlighter listeners are painted from a higher state, we can use
 * this as a helper to keep track of them
 */
export class ReadyTracker<T extends object> {
    #values = new Map<T, boolean>()
    #store: Writable<ReadyTrackerStatus>

    constructor(def : ReadyTrackerStatus = ReadyTrackerStatus.NONE) {
        this.#store = writable(def)
    }

    resetList(keys: T[], def = false) {
        this.#values.clear()
        keys.forEach((key) => this.#values.set(key, def))
        this.processChange()
    }

    updateList(keys: T[], def = false) {
        // Retain all keys
        const retained = [...this.#values.entries()].filter(([key]) => keys.includes(key))
        this.#values.clear()
        retained.forEach(([key, val]) => this.#values.set(key, val))
        keys.forEach((key) => {
            if (!this.#values.has(key)) {
                this.#values.set(key, def)
            }
        })
        this.processChange()
    }

    processChange() {
        // Send event if needed
        this.#store.set(this.getStatus())
    }

    getStatus(): ReadyTrackerStatus {
        const values = Array.from(this.#values.values())
        
        if (values.every(Boolean)) {
            return ReadyTrackerStatus.ALL
        } else if (values.some(Boolean)) {
            return ReadyTrackerStatus.SOME
        } else {
            return ReadyTrackerStatus.NONE
        }
    }

    emptyList() {
        this.#values.clear()
    }

    set(key: T, value: boolean) {
        if (!this.#values.has(key)) throw new Error("Value not found in ready tracker map!")
        this.#values.set(key, value)
        this.processChange()
    }

    subscribe: Readable<ReadyTrackerStatus>["subscribe"] = (fn: (value: ReadyTrackerStatus) => void) => {
        return this.#store.subscribe(fn)
    }
}
